import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
} from "@mui/material";
import { ButtonArrow } from "@thrivetribe/mvf.assets.icons.button-arrow";
import { Button } from "@thrivetribe/mvf.ui.atoms.button";
import { FormikNumberInput } from "@thrivetribe/mvf.ui.atoms.formik-number-input";
import { LoadingSpinner } from "@thrivetribe/mvf.ui.molecules.loading-spinner";
import {
  OnboardingPage,
  OnboardingPageBody,
  OnboardingPageContent,
  OnboardingPageCopy,
  OnboardingPageFooter,
} from "@thrivetribe/mvf.ui.organisms.onboarding-page";
import { differenceInYears, format, isAfter, isToday, isValid, parse } from "date-fns";
import { enGB } from "date-fns/locale";
import { ErrorMessage, Form as FormikForm, Formik, FormikHelpers, FormikValues } from "formik";
import { signupNavLink } from "Onboarding/utils";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { DateOfBirthValidationSchema } from "../OnboardingValidations";
import "./AgeChecker.scss";
import { determineErrorFieldName, formatValue, getInitialAgeValues } from "./utils";
import { useGetUserQuery, useUpdateUserMutation } from "redux/userApiSlice";
import { useEffect } from "react";
import { useUser } from "components/user/UserContext";
import { Gtag } from "configs/ReactGa";
import { ReactGa } from "ui/atoms/ReactGa";
import { ImagesUrl } from "configs/EnvironmentConfig";

const hasNoDateValue = (date: string, month: string, year: string) => {
  return !date && !month && !year;
};

const AgeChecker = () => {
  let navigate = useNavigate();
  let user = useUser();
  let { data: userObj, isLoading } = useGetUserQuery(user?.userId);
  let [updateProfile] = useUpdateUserMutation({
    fixedCacheKey: user?.userId,
  });

  const updateProfileData = async (dateOfBirth: string) => {
    if (userObj) {
      const updatedProfile = { ...userObj, personalDetails: { ...userObj.personalDetails, dateOfBirth } };
      await updateProfile(updatedProfile)
        .unwrap()
        .then(() => {
          Gtag("event", "dob_set", {
            user_id: user.userId,
            event_category: "account setup",
          });
          navigate("/portal-setup/ethnicity");
          return <></>;
        });
    }
  };

  useEffect(() => {
    if (user.isLegacy && userObj?.personalDetails?.dateOfBirth) {
      let age = differenceInYears(new Date(), new Date(userObj?.personalDetails?.dateOfBirth));
      if (age >= 18 && age < 99) {
        navigate("/portal-setup/ethnicity");
      }
    }
  }, [user, userObj, navigate]);

  const initialValues = getInitialAgeValues(userObj?.personalDetails.dateOfBirth ?? "") as FormikValues;

  const handleSubmit = async (values: FormikValues, formikhelpers: FormikHelpers<FormikValues>) => {
    formikhelpers.setSubmitting(true);
    if (hasNoDateValue(values.date, values.month, values.year)) {
      const errorMessage = "Enter your date of birth";
      formikhelpers.setSubmitting(false);
      formikhelpers.setErrors({ date: errorMessage, month: errorMessage, year: errorMessage });
      return;
    }
    const dateOfBirth = `${formatValue(values.date)}/${formatValue(values.month)}/${formatValue(values.year)}`;
    const parsedDate = parse(dateOfBirth, "P", new Date(), { locale: enGB });
    const errorMessage = "Double check and enter a valid date of birth";
    if (parsedDate.toString() !== "Invalid Date") {
      const isValidDate = isValid(parsedDate);
      const age = differenceInYears(new Date(), parsedDate);
      if (isValidDate) {
        if (isAfter(new Date(parsedDate), new Date()) || isToday(parsedDate) || age < 18 || age >= 99) {
          formikhelpers.setSubmitting(false);
          formikhelpers.setErrors({ date: errorMessage, month: errorMessage, year: errorMessage });
          return;
        } else {
          const formattedDate = format(parsedDate, "yyyy-MM-dd");
          await updateProfileData(formattedDate);
          formikhelpers.setSubmitting(false);
        }
      } else {
        formikhelpers.setSubmitting(false);
        formikhelpers.setErrors({ date: errorMessage, month: errorMessage, year: errorMessage });
      }
    } else {
      formikhelpers.setSubmitting(false);
      formikhelpers.setErrors({ date: errorMessage, month: errorMessage, year: errorMessage });
    }
  };

  const onBackClick = () => {
    navigate("/portal-setup");
  };

  if (isLoading) {
    return <LoadingSpinner fullscreen={true} />;
  }

  return (
    <div className="age-checker">
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={DateOfBirthValidationSchema}
        validateOnChange={true}
      >
        {({ errors, touched }) => {
          const fieldName = determineErrorFieldName(errors, touched);
          return (
            <FormikForm>
              <Helmet>
                <title>Set up - Age</title>
              </Helmet>
              <OnboardingPage
                user={user.displayName}
                links={signupNavLink}
                image={`${ImagesUrl}/group.webp`}
                title={"Set up"}
                onBackClick={onBackClick}
              >
                <OnboardingPageContent hasStickyFooter centralizeContent>
                  <OnboardingPageBody>
                    <OnboardingPageCopy>
                      <h1>What's your date of birth?</h1>
                    </OnboardingPageCopy>
                    <FormGroup className="onboarding-page__content--group">
                      <InputLabel htmlFor="date-of-birth">Enter your date of birth</InputLabel>
                      <Grid container spacing={2}>
                        <Grid item xs={2}>
                          <FormikNumberInput
                            className="date-input"
                            placeholder="DD"
                            pattern={/^([0-9]{0,2})/g}
                            id="date"
                            name="date"
                            label=""
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <FormikNumberInput
                            className="month-input"
                            placeholder="MM"
                            pattern={/^([0-9]{0,2})/g}
                            id="month"
                            name="month"
                            label=""
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormikNumberInput
                            className="year-input"
                            placeholder="YYYY"
                            pattern={/^([0-9]{0,4})/g}
                            id="year"
                            name="year"
                            label=""
                          />
                        </Grid>
                      </Grid>

                      <ErrorMessage key={fieldName} className="Mui-error" name={fieldName} component={FormHelperText} />
                    </FormGroup>
                    <Accordion square={true} elevation={0} className="onboarding-page__accordion">
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="large" />}
                        aria-controls={`question-explanation`}
                      >
                        <span>None of our business?! Here’s why we ask.</span>
                      </AccordionSummary>
                      <AccordionDetails>
                        <span>
                          Looks like we didn't catch this when you first signed up. Why do we need to know? It helps us
                          with reporting (yawn), plus we want to remember your birthday!
                        </span>
                      </AccordionDetails>
                    </Accordion>
                  </OnboardingPageBody>
                  <OnboardingPageFooter>
                    <Button type="submit" variant="primary" color="primary">
                      Continue
                      <ButtonArrow />
                    </Button>
                  </OnboardingPageFooter>
                </OnboardingPageContent>
              </OnboardingPage>
            </FormikForm>
          );
        }}
      </Formik>
      <ReactGa eventName="dob_requested" eventParams={{ user_id: user.userId, event_category: "account setup" }} />
    </div>
  );
};

export default AgeChecker;
